import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, ArrowRight, Calculator, FileSearch, Receipt, ShieldCheck, Users, TrendingUp, Clock, Award, CheckCircle2, Star, ThumbsUp, TrendingUp as TrendUp, MessageCircle, ClipboardList, Search, Compass, Handshake, Building2, HeartPulse, HardHat, UtensilsCrossed, HandHeart, Briefcase } from "lucide-react";
import { img } from "@/lib/images";
import { VideoFacade } from "@/components/VideoFacade";
import { site } from "@/lib/site";
import heroPortrait from "@/assets/hero-portrait.webp";
import heroPortrait480 from "@/assets/hero-portrait-480.webp";
import heroPortrait640 from "@/assets/hero-portrait-640.webp";
import accounting480 from "@/assets/service-accounting-480.webp";
import taxation480 from "@/assets/service-taxation-480.webp";
import videoPoster from "@/assets/about-video-poster.jpg";
import { canonicalLinks, routeMeta } from "@/lib/seo";

import heroBackdrop from "@/assets/hero-backdrop.svg";
import socialEfficiency from "@/assets/social/efficiency.webp";
import socialManageFinances from "@/assets/social/manage-finances.webp";
import socialPrecision from "@/assets/social/precision.webp";
import socialExpertSolutions from "@/assets/social/expert-solutions.webp";
import socialEfficientMgmt from "@/assets/social/efficient-mgmt.webp";
import socialAccountingBlocks from "@/assets/social/accounting-blocks.webp";
import socialFreeConsult from "@/assets/social/free-consult.webp";
import socialMaximizeProfits from "@/assets/social/maximize-profits.webp";
import socialSmallBusiness from "@/assets/social/small-business.webp";
import socialGrowBusiness from "@/assets/social/grow-business.webp";
import socialTailoredSolutions from "@/assets/social/tailored-solutions.webp";
import socialPrecisionAccuracy from "@/assets/social/precision-accuracy.webp";
import socialAnalysisReport from "@/assets/social/analysis-report.webp";
import socialOptimize from "@/assets/social/optimize.webp";
import socialTestimonialJoshua from "@/assets/social/testimonial-joshua.webp";
import reviewTulloch from "@/assets/review-tulloch.webp";
import { SmartImage } from "@/components/SmartImage";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      ...routeMeta("/"),
      { title: "Winter Garden CPA — Tax & Accounting | CCOB & Company PA" },
      { name: "description", content: "Winter Garden CPA firm offering tax, accounting, payroll, auditing, and IRS representation. 30+ years of trusted financial guidance." },
      { property: "og:title", content: "Winter Garden CPA — Tax & Accounting | CCOB & Company PA" },
      { property: "og:description", content: "Winter Garden CPA firm offering tax, accounting, payroll, auditing, and IRS representation. 30+ years of trusted financial guidance." },
    ],
    links: canonicalLinks("/"),
  }),
  component: HomePage,
});

const industries = [
  { label: "Real Estate", Icon: Building2 },
  { label: "Healthcare", Icon: HeartPulse },
  { label: "Construction", Icon: HardHat },
  { label: "Hospitality", Icon: UtensilsCrossed },
  { label: "Nonprofits", Icon: HandHeart },
  { label: "Professional Services", Icon: Briefcase },
];

function HomePage() {
  return (
    <>
      {/* HERO */}
      <section className="relative bg-white overflow-x-clip flex items-center pt-36 sm:pt-24 lg:pt-28 pb-16 lg:pb-24 xl:pb-28">

        <div className="hero-container-x relative z-10 grid lg:grid-cols-[1fr_minmax(400px,520px)] xl:grid-cols-[1fr_minmax(480px,620px)] 2xl:grid-cols-[1fr_minmax(560px,720px)] gap-10 lg:gap-12 xl:gap-20 items-center">
          {/* LEFT — copy */}
          <div className="flex flex-col items-start text-left">
            <div className="hidden xl:flex items-center gap-3 mb-6 text-[11px] tracking-[0.18em] uppercase text-foreground/55 font-medium">
              <span className="h-px w-10 bg-gold" />
              CCOB & Company PA · Winter Garden, FL
            </div>
            <div
              className="inline-flex items-center gap-2 bg-white rounded-full shadow-md ring-1 ring-border pl-3 pr-3.5 py-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600" />
              </span>
              <span className="text-[11px] sm:text-xs font-medium text-foreground">Available for new clients</span>
            </div>

            <h1
              className="font-display text-primary mt-4 sm:mt-6 font-normal tracking-[-0.025em] leading-[1.08]"
              style={{ fontSize: "clamp(1.75rem, 6vw, 4.25rem)", fontFeatureSettings: '"ss01", "liga", "dlig"' }}>
              <span className="block xl:whitespace-nowrap">Financial Consultation</span>
              <span className="block xl:whitespace-nowrap">for <span className="italic font-light text-gold-text">businesses & individuals</span>.</span>
            </h1>

            <p
              className="mt-5 sm:mt-7 max-w-lg xl:max-w-xl 2xl:max-w-2xl text-foreground/70 text-[15px] sm:text-base xl:text-[17px] 2xl:text-[18px] leading-[1.65]">
              Three decades of CPA counsel for the families and businesses of Central Florida — measured advice, considered tax strategy, and the kind of attention that only comes from a CPA you can actually reach.
            </p>

            <div
              className="mt-6 sm:mt-8 xl:mt-10 flex flex-col items-stretch sm:items-center gap-3 sm:flex-row sm:gap-6 w-full">
              <a href={`tel:${site.phoneRaw}`} className="btn-primary group !py-3 !px-6 w-full sm:w-auto justify-center">
                Call Us
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>

              <Link to="/contact" hash="consultation" className="btn-outline w-full sm:w-auto justify-center">
                Book Your Free Consultation
              </Link>
            </div>

          </div>

          {/* RIGHT — layered portrait (backdrop SVG + transparent PNG that pops out) */}
          <div className="relative w-full lg:w-auto">
            <div
              className="hero-portrait-frame relative mx-auto w-full">

              {/* Layer 1 — dark backdrop square (its own element) */}
              <div
                aria-hidden="true"
                style={{ backgroundColor: "#272d2a" }}
                className="absolute left-0 right-0 top-5 bottom-0 rounded-[10px] shadow-[0_40px_80px_-30px_rgba(15,40,30,0.45)] select-none pointer-events-none overflow-hidden"
              >
                {/* Subtle finance motif: bar chart + ascending trend line */}
                <svg
                  aria-hidden="true"
                  className="absolute left-0 right-0 top-0 w-full h-[820px]"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="xMidYMin meet"
                  viewBox="0 0 400 600"
                  fill="none"
                >
                  {/* baseline grid */}
                  <g stroke="#e8e2d1" strokeWidth="0.6" opacity="0.18">
                    <line x1="20" y1="120" x2="380" y2="120" />
                    <line x1="20" y1="200" x2="380" y2="200" />
                    <line x1="20" y1="280" x2="380" y2="280" />
                    <line x1="20" y1="360" x2="380" y2="360" />
                    <line x1="20" y1="440" x2="380" y2="440" />
                  </g>

                  {/* axis */}
                  <g stroke="#e8e2d1" strokeWidth="0.8" opacity="0.35">
                    <line x1="20" y1="80" x2="20" y2="520" />
                    <line x1="20" y1="520" x2="380" y2="520" />
                  </g>

                  {/* bar chart — ascending revenue, full width */}
                  <g fill="#e8e2d1" opacity="0.32">
                    <rect x="35"  y="430" width="34" height="90" />
                    <rect x="80"  y="380" width="34" height="140" />
                    <rect x="125" y="400" width="34" height="120" />
                    <rect x="170" y="320" width="34" height="200" />
                    <rect x="215" y="270" width="34" height="250" />
                    <rect x="260" y="220" width="34" height="300" />
                    <rect x="305" y="150" width="34" height="370" />
                  </g>

                  {/* gold trend line over the bars */}
                  <polyline
                    points="52,420 97,370 142,390 187,310 232,260 277,210 322,140"
                    stroke="#d4b572"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.95"
                  />
                  <g fill="#d4b572" opacity="1">
                    <circle cx="52"  cy="420" r="3" />
                    <circle cx="97"  cy="370" r="3" />
                    <circle cx="142" cy="390" r="3" />
                    <circle cx="187" cy="310" r="3" />
                    <circle cx="232" cy="260" r="3" />
                    <circle cx="277" cy="210" r="3" />
                    <circle cx="322" cy="140" r="4" />
                  </g>

                  {/* upward arrow tip */}
                  <path
                    d="M322 140 l -10 5 M322 140 l 5 10"
                    stroke="#d4b572"
                    strokeWidth="2"
                    strokeLinecap="round"
                    fill="none"
                  />
                </svg>
              </div>

              {/* Layer 2 — transparent portrait, overflows the top of the square */}
              <img
                src={heroPortrait}
                srcSet={`${heroPortrait480} 480w, ${heroPortrait640} 640w, ${heroPortrait} 1024w`}
                sizes="(max-width: 639px) 300px, (max-width: 767px) 380px, (max-width: 1023px) 460px, (max-width: 1279px) 520px, (max-width: 1535px) 540px, 580px"
                alt="Modern CPA advisor at CCOB And Company PA"
                width={1024}
                height={1536}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                style={{ filter: "drop-shadow(0 24px 28px rgba(15, 25, 20, 0.28))", height: "108%", transform: "translate(-50%, 0)" }}
                className="absolute left-1/2 bottom-0 w-auto max-w-none object-contain object-bottom pointer-events-none select-none z-10"
              />

              {/* Layer 3a — social proof card, left edge mid-height */}
              <div
                className="absolute top-[34%] sm:top-[38%] -left-2 sm:-left-4 lg:-left-10 z-20">
                <div
                  className="w-[160px] sm:w-[210px] md:w-[230px] rounded-2xl bg-white/95 backdrop-blur-md shadow-[0_25px_60px_-20px_rgba(15,40,30,0.45)] ring-1 ring-black/5 overflow-hidden">
                  <div className="flex items-center gap-3 px-3.5 py-3 sm:px-4 sm:py-3.5">
                    <div className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-full bg-primary/5 ring-1 ring-primary/10 grid place-items-center">
                      <Users className="h-5 w-5 text-gold" strokeWidth={1.75} />
                    </div>
                    <div className="text-left">
                      <div className="flex items-center gap-0.5 text-gold">
                        {[0,1,2,3,4].map((i) => (
                          <Star key={i} className="h-3 w-3 fill-gold" strokeWidth={0} />
                        ))}
                        <span className="ml-1 font-sans font-semibold text-[11px] text-primary">5.0</span>
                      </div>
                      <div className="mt-1 font-display text-[20px] sm:text-[22px] leading-none text-primary">
                        500<span className="text-gold">+</span>
                      </div>
                      <div className="mt-0.5 text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground leading-snug">
                        Clients served
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Layer 3b — stat card, overlaps bottom-right corner */}
              <div
                className="absolute bottom-6 sm:bottom-10 -right-2 sm:-right-4 lg:-right-10 z-20">
                <div
                  className="w-[160px] sm:w-[210px] md:w-[230px] rounded-2xl bg-white/95 backdrop-blur-md shadow-[0_25px_60px_-20px_rgba(15,40,30,0.45)] ring-1 ring-black/5 overflow-hidden">
                  <div className="flex items-center gap-3 px-3.5 py-3 sm:px-4 sm:py-3.5">
                    <div className="h-10 w-10 sm:h-11 sm:w-11 shrink-0 rounded-full bg-primary/5 ring-1 ring-primary/10 grid place-items-center">
                      <Award className="h-5 w-5 text-gold" strokeWidth={1.75} />
                    </div>
                    <div className="text-left">
                      <div className="font-display text-[24px] sm:text-[26px] leading-none text-primary">
                        30<span className="text-gold">+</span>
                      </div>
                      <div className="mt-1 text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] text-muted-foreground leading-snug">
                        Years fee-based<br />CPA advisory
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ABOUT */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center relative">
          <div className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold/40 via-primary/20 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
            <div className="absolute -top-3 -left-3 h-20 w-20 border-t-2 border-l-2 border-gold rounded-tl-2xl z-10" />
            <div className="absolute -bottom-3 -right-3 h-20 w-20 border-b-2 border-r-2 border-gold rounded-br-2xl z-10" />
            <VideoFacade videoId="xQm-Wt85nOM" title="CCOB And Company PA — Meet Locksley A. Cameron, CPA" poster={videoPoster} />
            <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              <span className="h-px w-8 bg-gold" />
              Watch Our Story
            </div>
          </div>
          <div>
            <div className="text-gold-text uppercase tracking-[0.25em] text-xs mb-4">About Our Firm</div>
            <h2 className="font-display text-3xl md:text-5xl text-primary">A Trusted Local Partner for Modern Financial Guidance</h2>
            <div className="gold-line mt-5" />
            <p className="mt-6 text-muted-foreground leading-relaxed">
              Led by Locksley A. Cameron, CPA, CCOB And Company PA has been serving Winter Garden and Central Florida for a decade — backed by 30 years of experience. We blend traditional CPA discipline with a personalized, client-first philosophy.
            </p>
            <div className="grid grid-cols-3 gap-4 mt-10">
              {[["30+","Years Experience"],["500+","Happy Clients"],["200+","Businesses Assisted"]].map(([v,l]) => (
                <div key={l} className="border-l-2 border-gold pl-3">
                  <div className="font-display text-2xl text-primary">{v}</div>
                  <div className="text-xs text-muted-foreground">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" className="btn-primary mt-10">More About Our Firm <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-16 md:py-24 bg-secondary/40">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="text-gold-text uppercase tracking-[0.25em] text-xs mb-3">Our Services</div>
            <h2 className="font-display text-3xl md:text-5xl text-primary">Comprehensive CPA Services Built Around You</h2>
            <div className="gold-line mt-5 mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-7">
            {[
              { title: "Accounting Services", srcSet: `${accounting480} 480w, ${img.accounting} 1024w`, desc: "Bookkeeping, payroll, financial analysis, and reporting designed for owners who want clarity, not clutter.", img: img.accounting, icon: Calculator, to: "/services/accounting" },
              { title: "Taxation Services", srcSet: `${taxation480} 480w, ${img.tax} 1024w`, desc: "Tax planning, preparation, filings, IRS audit support, resolution, and consulting — every tax matter handled in one place.", img: img.tax, icon: Receipt, to: "/services/taxation" },
              { title: "Part-Time CFO Services", srcSet: `${accounting480} 480w, ${img.accounting} 1024w`, desc: "Strategic financial leadership — budgeting, cash flow, profitability, and long-term planning — at a fraction of a full-time CFO's cost.", img: img.accounting, icon: Briefcase, to: "/services/part-time-cfo" },
            ].map((s) => (
              <div key={s.title} className="bg-card rounded-2xl ring-1 ring-border hover:ring-gold transition-all overflow-hidden flex flex-col">
                <div className="overflow-hidden">
                  <SmartImage src={s.img} srcSet={s.srcSet} sizes="(max-width: 767px) calc(100vw - 2rem), 33vw" width={1024} height={1024} alt={`${s.title}`} className="w-full aspect-[4/3] object-cover transition-transform duration-700 hover:scale-105" />
                </div>
                <div className="p-5 sm:p-7 flex flex-col flex-1">
                  <div className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-secondary text-primary mb-5"><s.icon className="h-4.5 w-4.5" strokeWidth={1.5} /></div>
                  <h3 className="font-display text-2xl text-primary">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground text-[15px] leading-relaxed flex-1">{s.desc}</p>
                  <Link to={s.to} className="mt-6 inline-flex items-center gap-2 text-gold-text font-medium text-sm hover:gap-3 transition-all">
                    Learn more about {s.title} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-16 md:py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Why Choose Us</div>
            <h2 className="font-display text-3xl md:text-5xl text-primary">Reasons Clients Trust CCOB</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              [Award,"CPA Licensed"],[Clock,"30 Years Experience"],[Users,"Personalized Guidance"],[ShieldCheck,"Trusted Local Reputation"],
              [TrendingUp,"Strategic Planning"],[CheckCircle2,"Transparent Communication"],[Phone,"Responsive Support"],[Calculator,"Flexible Consultations"],
            ].map(([I, l]: any) => (
              <div key={l} className="bg-card border border-border rounded-2xl p-4 sm:p-6 hover:border-gold transition-colors">
                <div className="h-10 w-10 sm:h-11 sm:w-11 rounded-full grid place-items-center bg-secondary text-primary"><I className="h-5 w-5" /></div>
                <div className="mt-3 sm:mt-4 font-display text-base sm:text-lg text-primary">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative py-20 md:py-28 bg-secondary/40 text-foreground overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-[0.06] pointer-events-none" />
        <div className="absolute -top-40 right-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-20 h-[420px] w-[420px] rounded-full bg-gold/5 blur-3xl pointer-events-none" />

        <div className="container-x relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16">
            <div className="lg:col-span-6">
              <div className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Our Process</div>
              <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] text-primary">
                A clear path to <span className="italic text-gold">financial clarity</span>.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                Every engagement follows the same calm, deliberate rhythm — so you always know what's next, who's responsible, and what success looks like.
              </p>
            </div>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute left-0 right-0 top-[42px] h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent" />

            <div className="grid lg:grid-cols-5 gap-10 lg:gap-6">
              {[
                { t: "Discovery Call", d: "A 30-minute conversation to understand your goals, current setup, and where we can help most.", Icon: MessageCircle },
                { t: "Service Scoping", d: "We map the right mix of accounting, audit, or tax services and share a transparent, fixed-scope proposal.", Icon: ClipboardList },
                { t: "Financial Assessment", d: "A full review of your books, filings, and structure to surface risks, savings, and quick wins.", Icon: Search },
                { t: "Strategy & Planning", d: "A tailored roadmap with clear milestones, deadlines, and owners — built around your fiscal calendar.", Icon: Compass },
                { t: "Ongoing Partnership", d: "Proactive check-ins, year-round tax planning, and a CPA on call when decisions can't wait.", Icon: Handshake },
              ].map((s, i) => (
                <div key={s.t} className="relative group">
                  <div className="relative flex items-center mb-7">
                    <div className="relative z-10 h-[84px] w-[84px] rounded-full bg-background border border-gold/40 grid place-items-center">
                      <s.Icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                    </div>
                    <div className="hidden lg:block flex-1 h-px bg-gold/20 ml-2" />
                  </div>

                  <div className="lg:pr-4">
                    <div className="font-display text-sm text-gold tabular-nums mb-2">{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="font-display text-xl md:text-2xl text-primary mb-3 group-hover:text-gold transition-colors">
                      {s.t}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">{s.d}</p>
                  </div>

                  <div className="mt-6 h-px w-10 bg-gold/50" />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-5 pt-10 border-t border-primary/10">
            <p className="font-display text-lg md:text-xl flex-1 text-primary">Ready to start with step one?</p>
            <a href={`tel:${site.phoneRaw}`} className="btn-gold">
              <Phone className="h-4 w-4" /> Call for your discovery call
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-24">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Testimonials</div>
            <h2 className="font-display text-3xl md:text-5xl text-primary">Trusted by Clients Across Central Florida</h2>
          </div>
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
            {[
              {
                img: reviewTulloch,
                name: "Extraterrestrial Tulloch",
                meta: "Local Guide · 21 reviews · 5 photos",
                age: "a year ago",
                quote: "One of the best accounting firms in the USA don't hessite to call MR Locksley he will get you what you need",
              },
              {
                img: null,
                name: "Samantha Walker",
                meta: "3 reviews",
                age: "5 years ago",
                quote: "I found Locksley Cameron CPA on Google while looking for a tax preparer for some assistance with my taxes. Before meeting with Locksley we spoke on the phone and he gave me advice and his expertise on my situation. After meeting with Locksley he made me feel comfortable and was able to help me prepare my taxes for 2019 in a timely manner. I recommend Locksley and will continue to use his services in the future.",
              },
            ].map((r) => (
              <div key={r.name} className="bg-card border border-border rounded-2xl p-7">
                <div className="flex items-center gap-3">
                  {r.img ? (
                    <SmartImage src={r.img} alt={r.name} width={63} height={63} sizes="44px" className="h-11 w-11 rounded-full object-cover" />
                  ) : (
                    <div className="h-11 w-11 rounded-full bg-primary/10 grid place-items-center text-primary font-display text-lg">
                      {r.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="font-medium text-primary">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.meta}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <div className="flex gap-0.5 text-gold">{[...Array(5)].map((_,i)=><Star key={i} className="h-4 w-4 fill-current" />)}</div>
                  <span className="text-xs text-muted-foreground">{r.age}</span>
                </div>
                <p className="mt-3 text-foreground leading-relaxed text-sm">{r.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FROM OUR FEED — branded social posts */}
      <section className="py-16 md:py-24 bg-secondary/30">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">From Our Feed</div>
              <h2 className="font-display text-3xl md:text-5xl text-primary max-w-xl">Insights & Updates from CCOB</h2>
            </div>
            <p className="text-muted-foreground max-w-md">
              A look at what we share with the Winter Garden community — financial tips, client stories, and reminders to plan ahead.
            </p>
          </div>
          <div className="columns-2 md:columns-3 lg:columns-4 xl:columns-5 gap-4 [column-fill:_balance]">
            {[
              { src: socialExpertSolutions, alt: "Expert Financial Solutions" },
              { src: socialPrecision, alt: "Financial Precision at Your Fingertips" },
              { src: socialMaximizeProfits, alt: "Maximize Profits — Your Trusted Partner" },
              { src: socialTailoredSolutions, alt: "Tailored Financial Solutions" },
              { src: socialGrowBusiness, alt: "Grow Your Business with Us" },
              { src: socialEfficientMgmt, alt: "Efficient Financial Management for Small Businesses" },
              { src: socialAccountingBlocks, alt: "Accounting, Taxation, Strategy" },
              { src: socialOptimize, alt: "Optimize Your Financial Processes" },
              { src: socialPrecisionAccuracy, alt: "Precision & Accuracy" },
              { src: socialFreeConsult, alt: "Contact us for a free consultation" },
              { src: socialEfficiency, alt: "Efficiency Meets Excellence" },
              { src: socialManageFinances, alt: "Manage Your Business Finances" },
              { src: socialSmallBusiness, alt: "Efficient Financial Management for Small Businesses" },
              { src: socialAnalysisReport, alt: "Financial Analysis Report" },
              { src: socialTestimonialJoshua, alt: "Client testimonial — Joshua S." },
            ].map((p, i) => (
              <a
                key={`${p.alt}-${i}`}
                href="https://www.facebook.com/lcamcpa"
                target="_blank"
                rel="noopener noreferrer"
               
                className={`group relative block mb-4 overflow-hidden rounded-xl ring-1 ring-border hover:ring-gold transition-all break-inside-avoid ${i >= 6 ? "hidden md:block" : ""}`}
              >
                <SmartImage
                  src={p.src}
                  alt={p.alt}
                  width={p.src === socialTestimonialJoshua ? 1080 : 940}
                  height={p.src === socialTestimonialJoshua ? 1080 : 788}
                  sizes="(max-width: 767px) 50vw, (max-width: 1023px) 33vw, (max-width: 1279px) 25vw, 20vw"
                  className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/20 transition-colors" />
              </a>
            ))}
          </div>
          <div className="mt-6 flex md:hidden justify-center">
            <a
              href="https://www.facebook.com/lcamcpa"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline"
            >
              View more on Facebook <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="container-x">
          <div className="relative rounded-3xl overflow-hidden bg-primary text-primary-foreground p-6 sm:p-10 md:p-16">
            <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <h2 className="font-display text-3xl md:text-4xl">Ready for clarity in your finances?</h2>
                <p className="mt-3 opacity-90">Book a consultation with Locksley A. Cameron, CPA today.</p>
              </div>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 md:justify-end">
                <a href={`tel:${site.phoneRaw}`} className="btn-gold w-full sm:w-auto justify-center">Call {site.phone}</a>
                <Link to="/contact" hash="consultation" className="btn-outline text-white w-full sm:w-auto justify-center">Schedule Consultation</Link>
              </div>
            </div>
            <div className="hidden md:block absolute -right-20 -bottom-20 h-72 w-72 rounded-full bg-gold/20" />
          </div>
        </div>
      </section>
    </>
  );
}
